import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../context/AuthContext";

const STATUS = {
  pending_review: {
    label: "Pending review",
    cls: "text-bronze border-bronze/40",
  },
  approved: {
    label: "Approved",
    cls: "text-emerald-700 border-emerald-700/40",
  },
  paid: { label: "Paid", cls: "text-emerald-700 border-emerald-700/40" },
  shipped: { label: "Shipped", cls: "text-ink border-ink/40" },
  declined: { label: "Declined", cls: "text-oxblood border-oxblood/40" },
};
const NEXT = {
  pending_review: ["approved", "declined"],
  approved: ["paid", "declined"],
  paid: ["shipped"],
  shipped: [],
  declined: ["pending_review"],
};

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("en-CA", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

const ProofPhoto = ({ order }) => {
  const [src, setSrc] = useState("");
  useEffect(() => {
    let url;
    api
      .get(`/admin/flags/orders/${order.id}/photo`, { responseType: "blob" })
      .then(({ data }) => {
        url = URL.createObjectURL(data);
        setSrc(url);
      })
      .catch(() => setSrc(""));
    return () => url && URL.revokeObjectURL(url);
  }, [order.id]);
  if (!src) return <div className="w-24 h-32 bg-ink/5" />;
  return (
    <a
      href={src}
      target="_blank"
      rel="noreferrer"
      data-testid={`flag-proof-${order.reference}`}
    >
      <img
        src={src}
        alt={`Original flag proof for ${order.reference}`}
        className="w-24 h-32 object-cover border border-ink/10"
      />
    </a>
  );
};

export const FlagOrdersPanel = () => {
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    api.get("/admin/flags/orders").then(({ data }) => setOrders(data));
  }, []);

  const setStatus = async (o, status) => {
    try {
      const { data } = await api.patch(`/admin/flags/orders/${o.id}/status`, {
        status,
      });
      setOrders((os) => os.map((x) => (x.id === data.id ? data : x)));
      toast.success(
        `${o.reference} marked ${STATUS[status].label.toLowerCase()}`,
      );
    } catch {
      toast.error("Could not update order");
    }
  };

  const pending = orders.filter((o) => o.status === "pending_review").length;

  return (
    <section data-testid="admin-flag-orders" className="space-y-4">
      <p
        data-testid="admin-flags-summary"
        className="font-mono text-[11px] uppercase tracking-[0.2em] text-mutedink"
      >
        {orders.length} flag requests · {pending} awaiting review
      </p>
      {orders.length === 0 && (
        <p data-testid="admin-flags-empty" className="text-sm text-mutedink">
          No flag requests yet.
        </p>
      )}
      {orders.map((o) => (
        <article
          key={o.id}
          data-testid={`flag-order-${o.reference}`}
          className="bg-white border border-ink/10 p-5 grid gap-4 sm:grid-cols-[auto_1fr_auto]"
        >
          <ProofPhoto order={o} />
          <div>
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="font-mono text-xs text-oxblood">
                {o.reference}
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-mutedink">
                {fmtDate(o.created_at)}
              </span>
              <span
                data-testid={`flag-order-status-${o.reference}`}
                className={`font-mono text-[10px] uppercase tracking-[0.15em] border px-2 py-0.5 ${STATUS[o.status].cls}`}
              >
                {STATUS[o.status].label}
              </span>
            </div>
            <p className="mt-2 font-serif text-lg text-ink">
              {o.quantity} × Hyrox {o.city} · {o.gender} {o.division} ·{" "}
              {o.age_group} · Season {o.season} · {o.ground}
              <span className="font-mono text-sm text-oxblood ml-3">
                ${o.total} {o.currency}
              </span>
            </p>
            <p className="mt-1 text-sm text-mutedink">
              {o.customer_name} · {o.customer_email}
              {o.customer_phone ? ` · ${o.customer_phone}` : ""}
            </p>
            <p className="text-sm text-mutedink">
              {o.address_line1}
              {o.address_line2 ? `, ${o.address_line2}` : ""}, {o.city_shipping}
              , {o.region} {o.postal_code}, {o.country}
            </p>
            {o.notes && (
              <p className="mt-1 text-sm italic text-mutedink">“{o.notes}”</p>
            )}
          </div>
          <div className="flex sm:flex-col gap-2 sm:items-end">
            {NEXT[o.status].map((s) => (
              <button
                key={s}
                type="button"
                data-testid={`flag-order-${o.reference}-mark-${s}`}
                onClick={() => setStatus(o, s)}
                className="font-mono text-[10px] uppercase tracking-[0.2em] border border-ink px-3 py-2 hover:bg-ink hover:text-parchment transition-colors whitespace-nowrap"
              >
                {s === "pending_review" ? "Reopen" : `Mark ${STATUS[s].label}`}
              </button>
            ))}
          </div>
        </article>
      ))}
    </section>
  );
};
